import { ChevronUpIcon } from "@chakra-ui/icons";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Divider,
  HStack,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { CopyText } from "../../components/Utilities/CopyText";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { StatusBadge } from "../../components/Utilities/StatusBadge";
import { CHERRY_PAY_CARD_STATUS_RULES } from "../../components/CherryPayCards/cherryPayCardStatusRules";
import {
  BOOLEAN_TRUE_RULES,
  BOOLEAN_FALSE_RULES,
} from "../../components/Utilities/StatusBadge";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
export const CherryPayCardDetail = () => {
  const { cardId } = useParams();
  const { data: card, isLoading } =
    useAdminApiRequest<CherryHubAdminApi.CherryPayCard>(
      (apiClient) => apiClient.getCherryPayCard(cardId!),
      [cardId]
    );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>CherryPay Card Detail</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <Breadcrumb variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/admin/cherrypay-cards">
                CherryPay Cards
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to={`/admin/cherrypay-cards/${cardId}`}>
                {card?.ExternalAccountId ?? cardId}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          {isLoading && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <SkeletonText w="220px" noOfLines={20} spacing="4" />
              <SkeletonText w="800px" noOfLines={2} spacing="4" />
            </VStack>
          )}
          {!isLoading && card && (
            <>
              <HStack w="100%" justifyContent="space-between">
                <Box pb={4}>
                  <Text fontSize="md" color="cherryUi.600">
                    {card.CardId}
                  </Text>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>
                      {card.ExternalAccountId ??
                        card.MaskedCardNumber ??
                        "Unknown"}
                    </b>
                  </Text>
                </Box>
                <VStack align="flex-end" spacing={2} pb={4}>
                  <Button
                    as={Link}
                    to={`/admin/cherrypay-cards/${cardId}/transactions`}
                    colorScheme="cherryButton"
                    isDisabled={!card.ExternalAccountId}
                  >
                    View Transactions
                  </Button>
                  <Button
                    as="a"
                    href={`${CHERRYHUBPORTAL_URL}/businesses/${card.BusinessId}/cards/${cardId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    colorScheme="cherryButton"
                    isDisabled={!card.BusinessId}
                  >
                    View in Portal
                  </Button>
                </VStack>
              </HStack>
              <VStack alignItems="flex-start" w="100%" pb={6}>
                <ViewControl label="Business ID">
                  <Box
                    flex={3}
                    sx={{
                      marginLeft: "-4px !important",
                      marginInlineStart: "-4px !important",
                    }}
                  >
                    {(card.BusinessId != null &&
                      String(card.BusinessId).trim() !== "" && (
                        <>
                          <PreviewButton
                            url={`/admin/businesses/${card.BusinessId}`}
                          >
                            {card.BusinessId}
                          </PreviewButton>
                          <CopyText ml={1} text={card.BusinessId}>
                            Copy
                          </CopyText>
                        </>
                      )) || (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl label="Card ID">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    textAlign="left"
                    alignSelf="flex-start"
                  >
                    {card.CardId != null &&
                    String(card.CardId).trim() !== "" ? (
                      <CopyText text={card.CardId} adjustMargin>
                        {card.CardId}
                      </CopyText>
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl label="External Account ID">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    textAlign="left"
                    alignSelf="flex-start"
                  >
                    {card.ExternalAccountId != null &&
                    String(card.ExternalAccountId).trim() !== "" ? (
                      <CopyText text={card.ExternalAccountId} adjustMargin>
                        {card.ExternalAccountId}
                      </CopyText>
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl
                  label="Masked Card Number"
                  value={
                    card.MaskedCardNumber != null &&
                    String(card.MaskedCardNumber).trim() !== ""
                      ? card.MaskedCardNumber
                      : "-"
                  }
                />
                <ViewControl label="Status">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    alignSelf="flex-start"
                  >
                    {card.Card?.Status != null &&
                    String(card.Card.Status).trim() !== "" ? (
                      <StatusBadge
                        value={card.Card.Status}
                        rules={CHERRY_PAY_CARD_STATUS_RULES}
                      />
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <Divider
                  variant="section"
                  marginInlineStart="-1rem !important"
                  w="calc(100% + 2rem)"
                />
                <ViewControl label="Member ID">
                  <Box
                    flex={3}
                    sx={{
                      marginLeft: "-4px !important",
                      marginInlineStart: "-4px !important",
                    }}
                  >
                    {card.MemberId != null &&
                    String(card.MemberId).trim() !== "" ? (
                      <>
                        <PreviewButton
                          url={`${CHERRYHUBPORTAL_URL}/businesses/${card.BusinessId}/members/${card.MemberId}`}
                          linkProps={{ target: "_blank" }}
                        >
                          {card.MemberId}
                        </PreviewButton>
                        <CopyText ml={1} text={card.MemberId}>
                          Copy
                        </CopyText>
                      </>
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl label="Mobile Number">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    textAlign="left"
                    alignSelf="flex-start"
                  >
                    {card.MobileNumber != null &&
                    String(card.MobileNumber).trim() !== "" ? (
                      <CopyText text={card.MobileNumber} adjustMargin>
                        {card.MobileNumber}
                      </CopyText>
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl
                  label="First Name"
                  value={
                    card.Card?.FName != null &&
                    String(card.Card?.FName).trim() !== ""
                      ? card.Card?.FName
                      : "-"
                  }
                />
                <ViewControl
                  label="Last Name"
                  value={
                    card.Card?.LName != null &&
                    String(card.Card?.LName).trim() !== ""
                      ? card.Card?.LName
                      : "-"
                  }
                />
                <ViewControl label="Email Address">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    textAlign="left"
                    alignSelf="flex-start"
                  >
                    {card.Card?.Email != null &&
                    String(card.Card?.Email).trim() !== "" ? (
                      <CopyText text={card.Card?.Email} adjustMargin>
                        {card.Card?.Email}
                      </CopyText>
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <Divider
                  variant="section"
                  marginInlineStart="-1rem !important"
                  w="calc(100% + 2rem)"
                />
                <ViewControl label="SMS Sent">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    alignSelf="flex-start"
                  >
                    {typeof card.SMSSent === "boolean" ? (
                      <StatusBadge
                        value={card.SMSSent ? "Yes" : "No"}
                        rules={BOOLEAN_TRUE_RULES}
                      />
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl label="Hide In CherryPay App">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    alignSelf="flex-start"
                  >
                    {typeof card.HideInCherryPayApp === "boolean" ? (
                      <StatusBadge
                        value={card.HideInCherryPayApp ? "Yes" : "No"}
                        rules={BOOLEAN_FALSE_RULES}
                      />
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl label="Skip Control Pay Policy Test">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    alignSelf="flex-start"
                  >
                    {typeof card.SkipControlPayPolicyTest === "boolean" ? (
                      <StatusBadge
                        value={card.SkipControlPayPolicyTest ? "Yes" : "No"}
                        rules={BOOLEAN_FALSE_RULES}
                      />
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl
                  label="Apply Control Pay Card Policy Name"
                  value={
                    card.ApplyControlPayCardPolicyName != null &&
                    String(card.ApplyControlPayCardPolicyName).trim() !== ""
                      ? card.ApplyControlPayCardPolicyName
                      : "-"
                  }
                />
                <Divider
                  variant="section"
                  marginInlineStart="-1rem !important"
                  w="calc(100% + 2rem)"
                />
                <ViewControl label="Card Issuance Fee Transaction ID">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    textAlign="left"
                    alignSelf="flex-start"
                  >
                    {card.CherryPayCardIssuanceFeeId != null &&
                    String(card.CherryPayCardIssuanceFeeId).trim() !== "" ? (
                      <CopyText
                        text={card.CherryPayCardIssuanceFeeId}
                        adjustMargin
                      >
                        {card.CherryPayCardIssuanceFeeId}
                      </CopyText>
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl label="Card Issuance Transaction Trace ID">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    textAlign="left"
                    alignSelf="flex-start"
                  >
                    {card.TraceId != null &&
                    String(card.TraceId).trim() !== "" ? (
                      <CopyText text={card.TraceId} adjustMargin>
                        {card.TraceId}
                      </CopyText>
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl label="CherryPay Card Order ID">
                  <Box
                    flex={3}
                    display="flex"
                    justifyContent="flex-start"
                    textAlign="left"
                    alignSelf="flex-start"
                  >
                    {card.CherryPayCardOrderId != null &&
                    String(card.CherryPayCardOrderId).trim() !== "" ? (
                      <CopyText text={card.CherryPayCardOrderId} adjustMargin>
                        {card.CherryPayCardOrderId}
                      </CopyText>
                    ) : (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <ViewControl
                  label="Issued By User"
                  value={
                    card.IssuedByUser != null &&
                    String(card.IssuedByUser).trim() !== ""
                      ? card.IssuedByUser
                      : "-"
                  }
                />
                <ViewControl label="Issued By User ID">
                  <Box
                    flex={3}
                    sx={{
                      marginLeft: "-4px !important",
                      marginInlineStart: "-4px !important",
                    }}
                  >
                    {(card.IssuedByUserId != null &&
                      String(card.IssuedByUserId).trim() !== "" && (
                        <>
                          <PreviewButton
                            url={`/admin/users/${card.IssuedByUserId}`}
                          >
                            {card.IssuedByUserId}
                          </PreviewButton>
                          <CopyText ml={1} text={card.IssuedByUserId}>
                            Copy
                          </CopyText>
                        </>
                      )) || (
                      <Text as="span" fontSize="sm">
                        -
                      </Text>
                    )}
                  </Box>
                </ViewControl>
                <Divider
                  variant="section"
                  marginInlineStart="-1rem !important"
                  w="calc(100% + 2rem)"
                />
                <ViewControl
                  label="Date Last Updated by Notification"
                  value={card.EventTime ?? "-"}
                />
                <ViewControl
                  label="Date Created"
                  value={card.Meta?.DateCreated ?? "-"}
                />
                <ViewControl
                  label="Date Updated"
                  value={card.Meta?.DateUpdated ?? "-"}
                />
              </VStack>
              <Accordion
                allowMultiple
                allowToggle
                w="100%"
                variant="section"
                defaultIndex={0}
              >
                {card.Card && (
                  <AccordionItem>
                    {({ isExpanded }) => {
                      const cardDetails = card.Card!;
                      return (
                        <>
                          <h2>
                            <AccordionButton>
                              <Box flex="1" textAlign="left" fontWeight="bold">
                                Card Details
                              </Box>
                              {isExpanded ? (
                                <ChevronUpIcon color="white" fontSize="xl" />
                              ) : (
                                <AccordionIcon />
                              )}
                            </AccordionButton>
                          </h2>
                          <AccordionPanel width="100%">
                            <VStack w="100%" alignItems="flex-start">
                              <ViewControl label="Company ID">
                                <Box
                                  flex={3}
                                  display="flex"
                                  justifyContent="flex-start"
                                  textAlign="left"
                                  alignSelf="flex-start"
                                >
                                  {cardDetails.Company != null &&
                                  String(cardDetails.Company).trim() !== "" ? (
                                    <CopyText
                                      text={cardDetails.Company}
                                      adjustMargin
                                    >
                                      {cardDetails.Company}
                                    </CopyText>
                                  ) : (
                                    <Text as="span" fontSize="sm">
                                      -
                                    </Text>
                                  )}
                                </Box>
                              </ViewControl>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl label="Program ID">
                                <Box
                                  flex={3}
                                  display="flex"
                                  justifyContent="flex-start"
                                  textAlign="left"
                                  alignSelf="flex-start"
                                >
                                  {card.ProgramId != null &&
                                  String(card.ProgramId).trim() !== "" ? (
                                    <CopyText
                                      text={card.ProgramId.toString()}
                                      adjustMargin
                                    >
                                      {card.ProgramId}
                                    </CopyText>
                                  ) : (
                                    <Text as="span" fontSize="sm">
                                      -
                                    </Text>
                                  )}
                                </Box>
                              </ViewControl>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Activation Code"
                                value={
                                  cardDetails.Code != null &&
                                  String(cardDetails.Code).trim() !== ""
                                    ? cardDetails.Code
                                    : "-"
                                }
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Date Card Redeemed"
                                value={cardDetails.Redeemed ?? "-"}
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Date Offer Expires"
                                value={cardDetails.Exp ?? "-"}
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Plastic Expiry"
                                value={cardDetails.PlasticExpiry ?? "-"}
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Card Program Type"
                                value={
                                  card.CardProgramType != null &&
                                  String(card.CardProgramType).trim() !== ""
                                    ? card.CardProgramType
                                    : "-"
                                }
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl label="MDES Config ID">
                                <Box
                                  flex={3}
                                  display="flex"
                                  justifyContent="flex-start"
                                  textAlign="left"
                                  alignSelf="flex-start"
                                >
                                  {cardDetails.MDESConfigId != null &&
                                  String(cardDetails.MDESConfigId).trim() !==
                                    "" ? (
                                    <CopyText
                                      text={cardDetails.MDESConfigId}
                                      adjustMargin
                                    >
                                      {cardDetails.MDESConfigId}
                                    </CopyText>
                                  ) : (
                                    <Text as="span" fontSize="sm">
                                      -
                                    </Text>
                                  )}
                                </Box>
                              </ViewControl>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Plastic Enabled"
                                value={
                                  cardDetails.PlasticEnabled ? "Yes" : "No"
                                }
                              />
                            </VStack>
                          </AccordionPanel>
                        </>
                      );
                    }}
                  </AccordionItem>
                )}
                {card.Batch && (
                  <AccordionItem>
                    {({ isExpanded }) => {
                      const batch = card.Batch!;
                      return (
                        <>
                          <h2>
                            <AccordionButton>
                              <Box flex="1" textAlign="left" fontWeight="bold">
                                Batch
                              </Box>
                              {isExpanded ? (
                                <ChevronUpIcon color="white" fontSize="xl" />
                              ) : (
                                <AccordionIcon />
                              )}
                            </AccordionButton>
                          </h2>
                          <AccordionPanel width="100%">
                            <VStack w="100%" alignItems="flex-start">
                              <ViewControl label="Batch ID">
                                <Box
                                  flex={3}
                                  display="flex"
                                  justifyContent="flex-start"
                                  textAlign="left"
                                  alignSelf="flex-start"
                                >
                                  {batch.Id != null &&
                                  String(batch.Id).trim() !== "" ? (
                                    <CopyText text={batch.Id} adjustMargin>
                                      {batch.Id}
                                    </CopyText>
                                  ) : (
                                    <Text as="span" fontSize="sm">
                                      -
                                    </Text>
                                  )}
                                </Box>
                              </ViewControl>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl label="Program">
                                <Box
                                  flex={3}
                                  display="flex"
                                  justifyContent="flex-start"
                                  textAlign="left"
                                  alignSelf="flex-start"
                                >
                                  {batch.Program != null &&
                                  String(batch.Program).trim() !== "" ? (
                                    <CopyText text={batch.Program} adjustMargin>
                                      {batch.Program}
                                    </CopyText>
                                  ) : (
                                    <Text as="span" fontSize="sm">
                                      -
                                    </Text>
                                  )}
                                </Box>
                              </ViewControl>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Created At"
                                value={batch.Created ?? "-"}
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Card Count"
                                value={
                                  batch.Count != null
                                    ? String(batch.Count)
                                    : "-"
                                }
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Total Value"
                                value={
                                  batch.Value != null
                                    ? String(batch.Value)
                                    : "-"
                                }
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Card Batch Processor"
                                value={batch.CardBatchProcessor ?? "-"}
                              />
                            </VStack>
                          </AccordionPanel>
                        </>
                      );
                    }}
                  </AccordionItem>
                )}
              </Accordion>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
